
import React from "react";
import { FIELD_TYPES } from "@hyro-components/fields/utils";

import {
  getMandatoryMessageForType,
  NO_OPTIONS_ERROR_MESSAGE,
  NO_OPTIONS_MESSAGE,
  STATUS_TYPES,
  VAGUE_NO_OPTIONS_MESSAGE,
  STATUS,
  STATUS_SEVERITY,
  WRAPPER_OBJECT_NAME,
  VALIDATION_TYPES,
  VALIDATIONS_SEVERITY
} from "./consts";

export const isBool = (value) => typeof value === "boolean";

export const isPrimitiveType = (type) => (type === FIELD_TYPES.TEXT
  || type === FIELD_TYPES.PHONE
  || type === FIELD_TYPES.URL
  || type === FIELD_TYPES.INT
  || type === FIELD_TYPES.BOOL
  || type === FIELD_TYPES.SECRET
);

const toEmptyRef = (collectionId) => `${collectionId?.split("/").at(0)}/`;

const isEmptyRef = (value) => typeof value === "string" && value.endsWith("/");

export const transformFieldSpecToConfig = (fieldSpec, fieldConfig) => {
  if (!fieldSpec) return null;

  if (fieldSpec?.fields?.length) {
    return fieldSpec.fields.map((field) => {
      const { key, type, collection_id, default: configuration, fields } = field;

      const existingField = fieldConfig?.find(item => item?.key === key);
      const baseField = { key, type };

      if (existingField) {
        if (type === WRAPPER_OBJECT_NAME && fields?.length && Array.isArray(existingField.configuration)) {
          return {
            ...baseField,
            configuration: transformFieldSpecToConfig({ fields }, existingField.configuration)
          };
        }
        return existingField;
      }

      if (type === WRAPPER_OBJECT_NAME && fields?.length) {
        return {
          ...baseField,
          configuration: transformFieldSpecToConfig({ fields })
        };
      }

      if (type === FIELD_TYPES.COLLECTION_REF) {
        return { ...baseField, configuration: toEmptyRef(collection_id) };
      }

      return {
        ...baseField,
        configuration: configuration ?? null
      };
    });
  }

  if (fieldSpec?.type === FIELD_TYPES.COLLECTION_REF) {
    return {
      key: fieldSpec?.key || null,
      type: fieldSpec.type,
      configuration: toEmptyRef(fieldSpec.collection_id)
    };
  }

  return {
    key: fieldSpec?.key || null,
    type: fieldSpec?.type || null,
    configuration: fieldSpec?.default ?? null
  };
};

export const determineStatusForTableRow = (liveValue, value, publishValue, status, incomplete, invalid, duplicate, unassigned) => {
  if (duplicate || status === STATUS_TYPES.DUPLICATE) {
    return STATUS_TYPES.DUPLICATE;
  }

  if (invalid || status === STATUS_TYPES.INVALID) {
    return STATUS_TYPES.INVALID;
  }

  if (incomplete || status === STATUS_TYPES.INCOMPLETE) {
    return STATUS_TYPES.INCOMPLETE;
  }

  if (unassigned || status === STATUS_TYPES.UNASSIGNED) {
    return STATUS_TYPES.UNASSIGNED;
  }

  if (status === STATUS_TYPES.DRAFT) {
    return STATUS_TYPES.DRAFT;
  }

  if (JSON.stringify(value) !== JSON.stringify(publishValue)) {
    return STATUS_TYPES.DRAFT;
  }

  if (status === STATUS_TYPES.PENDING) {
    return STATUS_TYPES.PENDING;
  }

  if (JSON.stringify(liveValue) !== JSON.stringify(publishValue)) {
    return STATUS_TYPES.PENDING;
  }
  return STATUS_TYPES.LIVE;
};

export const noOptionList = (label, customMessage) => [
  {
    display_key: customMessage || (label ? NO_OPTIONS_MESSAGE.replace("$label", label) : VAGUE_NO_OPTIONS_MESSAGE),
    key: "no_options",
    disabled: true
  }
];

export const errorOptionList = (label) => [
  {
    display_key: NO_OPTIONS_ERROR_MESSAGE.replace("$label", label),
    key: "no_options_error",
    error: true
  }
];

export const extractKeysAndCollectionIdsIfRef = (obj) => {
  const result = [];

  const extract = (obj) => {
    if (obj?.options) {
      obj.options.forEach(field => {
        extract(field);
      });
    } else if (obj?.fields) {
      obj.fields.forEach(field => {
        extract(field);
      });
    } else if (obj?.type === FIELD_TYPES.COLLECTION_REF) {
      result.push({ key: obj.key, collection_id: obj.collection_id, query_params: obj.query_params });
    }
  };

  extract(obj);
  return result;
};

export const isItemMultiLanguageField = (field) => field?.type === FIELD_TYPES.MULTI_LANGUAGE_TEXT || field?.type === FIELD_TYPES.ACTION;

export const getRealFieldValue = (value, language) => (Object.prototype.hasOwnProperty.call(value || {}, "text") && value?.text !== "")
  || (Object.prototype.hasOwnProperty.call(value || {}, language) && value?.[language] !== "")
  || value?.length > 0
  || (typeof value !== "object" && value)
  || Number.isFinite(value);

// eslint-disable-next-line consistent-return
export const isMandatoryField = ({ type, is_mandatory, value, language, collectionLanguages }) => {
  if (type === FIELD_TYPES.MULTI_LANGUAGE_TEXT) {
    let mandatoryFields = {};

    if (value) {
      collectionLanguages?.forEach((language) => {
        const val = getRealFieldValue(value[language], language);
        if (is_mandatory && !val) mandatoryFields = { ...mandatoryFields, ...getMandatoryMessageForType(type, language) };
      });
    }

    return Object.keys(mandatoryFields).length === 0 ? "" : mandatoryFields;
  } if (type === FIELD_TYPES.FILE) {
    return !value?.file_name || !value?.file_type || !value?.upload_date ? getMandatoryMessageForType(type) : "";
  }

  if (type === FIELD_TYPES.COLLECTION_REF && isEmptyRef(value)) {
    return is_mandatory ? getMandatoryMessageForType(type) : "";
  }

  const val = getRealFieldValue(value, language);
  if (is_mandatory && !val && !isBool(value)) return getMandatoryMessageForType(type);
  if (!val && !isBool(value)) return "";
};

export const getMandatoryStar = (isMandatory) => isMandatory ? <span style={{ color: "#EF404B" }}> *</span> : null;

export const doesRowEqualSpec = (row, spec) => {
  const specConfiguration = spec.fields;

  return specConfiguration.every((config) => {
    const value = row[config.display_key];

    switch (config.type) {
      case FIELD_TYPES.MULTI_LANGUAGE_TEXT:
        return value && Object.keys(value).every((language) => value?.[language] === config?.default?.[language]);

      case FIELD_TYPES.COLLECTION_REF:
      case FIELD_TYPES.LIST:
        return JSON.stringify(value) === JSON.stringify(config?.default);

      case FIELD_TYPES.CHOOSE_ONE:
        return value === config?.default?.key || value === config?.default?.configuration?.value;

      case FIELD_TYPES.FILE:
        return true;

      default:
        return JSON.stringify(value) === JSON.stringify(config?.default?.value);
    }
  });
};

export const isRowUnique = (row, rows) => {

  const cleanRow = ({ index, id, [STATUS]: status, [STATUS_SEVERITY]: severity, fields, spec, path, ...rest }) => rest;

  const cleanRows = rows.filter(({ id }) => id !== row?.id).map(cleanRow);
  const cleanRowToCheck = cleanRow(row);

  if (cleanRows.length === 0) return true;
  return cleanRows.every((collectionRow) => Object.keys(cleanRowToCheck).some((key) => JSON.stringify(cleanRowToCheck[key]) !== JSON.stringify(collectionRow[key])));
};

export const isProtectedKnowledgeSource = (collection) => collection?.spec?.is_protected === true;

export const filterOutRowsToDelete = (collection, rowIdsToDelete) => {
  const filterConfiguration = (configuration) => configuration.map((config) => {
    if (config.type === FIELD_TYPES.LIST) {
      return {
        ...config,
        configuration: filterConfiguration(
          config.configuration.filter((innerConfig) => !rowIdsToDelete.includes(innerConfig.key))
        )
      };
    }
    return config;
  });

  return {
    ...collection,
    configuration: {
      ...collection.configuration,
      configuration: filterConfiguration(collection.configuration.configuration)
    }
  };
};

export const getValidationMessage = (validationResults = {}, configurableKey, severity) => {
  const validationKeys = Object.entries(validationResults)
    .filter(([key, results]) =>
      key.includes(configurableKey) && (!severity || results.some(({ error_severity_level }) => error_severity_level === severity))
    )
    .map(([key, results]) => ({ key, results }));

  return validationKeys.length ? validationKeys : null;
};

// Server validation keys: "{collectionName}/{specKey}/{instanceKey}/{fieldPath...}"
export const scopeValidationResultsToInstance = (validationResults, instanceKey) => {
  if (!instanceKey) return {};
  return Object.entries(validationResults || {}).reduce((acc, [key, results]) => {
    const [, , keyInstance, ...fieldPath] = key.split("/");
    if (keyInstance === instanceKey) {
      acc[fieldPath.join("/")] = results;
    }
    return acc;
  }, {});
};

export const getValidationTypes = (validationResults, configurableKey) => Object.entries(validationResults || {}).reduce((result, [key, results]) => {
  if (key.includes(configurableKey)) {
    results.forEach(({ type, details }) => {
      result[type] = details;
    });
  }
  return result;
}, {});

export const VALIDATION_TYPE_TO_STATUS = {
  [VALIDATION_TYPES.BAD_CONFIGURATION]: STATUS_TYPES.INVALID,
  [VALIDATION_TYPES.MISSING_MANDATORY_CONFIGURATION]: STATUS_TYPES.INCOMPLETE,
  [VALIDATION_TYPES.UNASSIGNED]: STATUS_TYPES.UNASSIGNED
};

const VALIDATION_TYPE_RANK = Object.keys(VALIDATION_TYPE_TO_STATUS);
const SEVERITY_RANK = [VALIDATIONS_SEVERITY.WARNING, VALIDATIONS_SEVERITY.ERROR];

const severityRank = ({ error_severity_level }) => Math.max(SEVERITY_RANK.indexOf(error_severity_level), 0);
const typeRank = ({ type }) => {
  const index = VALIDATION_TYPE_RANK.indexOf(type);
  return index === -1 ? -1 : VALIDATION_TYPE_RANK.length - index;
};

export const pickDominantValidation = (entries = []) => (entries || []).reduce((dominant, entry) => {
  if (typeRank(entry) === -1) return dominant;
  if (!dominant) return entry;
  if (severityRank(entry) !== severityRank(dominant)) return severityRank(entry) > severityRank(dominant) ? entry : dominant;
  return typeRank(entry) > typeRank(dominant) ? entry : dominant;
}, null);

export const getKnowledgeValidationEntries = (knowledgeValidations, schemaType) => {
  const entry = schemaType ? knowledgeValidations?.[schemaType] : null;
  if (!entry) return [];
  return [...(entry.type || []), ...Object.values(entry.rows || {}).flat()];
};

export const getDominantValidation = (validationResults, configurableKey, extraEntries = []) => {
  if (!configurableKey) return null;
  const entries = Object.entries(validationResults || {})
    .filter(([key]) => key.includes(configurableKey))
    .flatMap(([, results]) => (Array.isArray(results) ? results : []));
  const dominant = pickDominantValidation([...entries, ...extraEntries]);
  return dominant ? { ...dominant, status: VALIDATION_TYPE_TO_STATUS[dominant.type] } : null;
};

const LOCAL_STATUS_TO_VALIDATION = {
  [STATUS_TYPES.INVALID]: { type: VALIDATION_TYPES.BAD_CONFIGURATION, error_severity_level: VALIDATIONS_SEVERITY.ERROR },
  [STATUS_TYPES.INCOMPLETE]: { type: VALIDATION_TYPES.MISSING_MANDATORY_CONFIGURATION, error_severity_level: VALIDATIONS_SEVERITY.ERROR }
};

export const resolveRowStatus = (localStatus, rowValidation) => {
  const localValidation = LOCAL_STATUS_TO_VALIDATION[localStatus];
  if (localStatus === STATUS_TYPES.DUPLICATE || !rowValidation) return { status: localStatus, severity: undefined };
  const dominant = pickDominantValidation([localValidation, rowValidation].filter(Boolean));
  if (dominant !== rowValidation) return { status: localStatus, severity: undefined };
  return { status: rowValidation.status, severity: rowValidation.error_severity_level };
};

export const getValidationsByType = (validationResults, type) => {
  if (!validationResults) return [];
  const validationsAsObject = Object.entries(validationResults).filter(([key]) => key.includes("root")).reduce((acc, [key, errors]) => {
    const [fieldType] = key.split("/");

    if (!acc[fieldType]) {
      acc[fieldType] = 0;
    }
    acc[fieldType] += errors.filter(error => error.type === type).length;
    return acc;
  }, {});
  return Object.entries(validationsAsObject).map(([fieldType, amount]) => ({ fieldType, amount }));
};

export const getLanguages = (input) => {
  const languages = input?.languages ?? input; // Check if input has a "languages" key, otherwise use input directly
  if (!Array.isArray(languages)) return { list: [], default: null };
  return {
    list: languages?.map(lang => lang.language),
    default: languages?.find(lang => lang.is_default)?.language
  };
};
